import './project-footer.scss';

import { ThunkDispatch } from 'redux-thunk';

import * as Model from '../../../models';
import * as I18n from '../../../models/i18n';
import * as OligoActions from '../../../actions/oligo-actions';

import { countOligos } from '../../../models/util';

export const PROJECT_FOOTER_NAME: string = 'projectFooter';

export interface ProjectFooterOptions {
  project: Model.ProjectItemOrNull;
}

export interface ProjectFooterProperties {
  reachedMaxNoOfOligos: boolean;
}

type I18nAware = {
  ADD_OLIGO_LABEL: string;
  MAX_OLIGO_REACHED_HINT: string;
  NO_OF_OLIGO_LABEL: string;
};

export class ProjectFooterController implements ng.IComponentController, I18nAware, ProjectFooterOptions, ProjectFooterProperties, OligoActions.OligoAddSupport {

  public project: Model.ProjectItemOrNull = null;
  public addOligo: OligoActions.OligoAdd | null = null;
  public numOligoToAdd: string = '1';
  public loading: boolean = false;
  public reachedMaxNoOfOligos: boolean = false;

  public ADD_OLIGO_LABEL: string = 'Add More Oligo(s)';
  public MAX_OLIGO_REACHED_HINT: string = `You have reached or exceeded the maximum limit of {{${I18n.PlaceHolderKeys.MAX_OLIGO_COUNT}}} oligos.`;
  public NO_OF_OLIGO_LABEL: string = 'No. of oligo(s) to add:';

  constructor($scope: ng.IScope, $ngRedux: Model.INgRedux, oligoActions: OligoActions.OligoActions) {
    const bindDispatchers = (dispatch: ThunkDispatch<Model.RootState, object, Model.ReduxAction<object>>) => {
      this.addOligo = (request: OligoActions.OligoAddFnRequest) => {
        return dispatch(oligoActions.addOligo(request));
      };
    };
    const unsubscribe = $ngRedux.connect(this.mapStateToThis.bind(this), bindDispatchers)(this);
    $scope.$on('$destroy', unsubscribe);
  }

  public doAddOligo(): void {
    if (!this.addOligo || !this.project) {
      return;
    }
    if (this.cannotAddNewOligo()) {
      return;
    }
    this.onQuantityBlur();
    const quantity = parseInt(this.numOligoToAdd, 10);
    const { index, oligos, preference, id: projectId } = this.project;
    this.loading = true;
    this.addOligo({
      preference,
      quantity,
      projectId,
      projectIdx: index,
      nextIdx: oligos.length,
    }).finally(() => this.loading = false);
  }

  public $onDestroy(): void {
    this.project = null;
    this.addOligo = null;
  }

  public onQuantityBlur(): void {
    const quantity = 1;
    try {
      const value = parseInt(this.numOligoToAdd, 10);
      if (isNaN(value) || value <= 0) {
        this.numOligoToAdd = quantity.toString();
      }
    } catch (e) {
      this.numOligoToAdd = quantity.toString();
    }
  }

  public onQuantityKeyPress($event: JQueryKeyEventObject): void {
    const { which, keyCode } = $event;
    const key = which || keyCode;
    const isArrowKey = key >= 37 && key <= 40 ;
    const isBackspaceKey = key === 8;
    const isDeleteKey = key === 46;
    const isNumKey = key >= 48 && key <= 57;
    const isValidKey = isArrowKey || isBackspaceKey || isDeleteKey || isNumKey;
    if (!isValidKey) {
      $event.preventDefault();
    }
  }

  public cannotAddNewOligo(): boolean {
    return  this.loading || this.reachedMaxNoOfOligos;
  }

  private mapStateToThis(state: Model.RootState): I18nAware & ProjectFooterProperties {
    const { projects, world } = state;
    const { vocabulary, maxNoOfOligos } = world;
    const totalNoOfOligos: number = countOligos(projects);
    const reachedMaxNoOfOligos: boolean = totalNoOfOligos >= maxNoOfOligos;
    let maxOligoReachedHint: string = vocabulary[I18n.DictionaryKeys.PROJECT_FOOTER_ADD_OLIGO_EXCEED_MAX_OLIGO_HINT] || this.MAX_OLIGO_REACHED_HINT;
    maxOligoReachedHint = I18n.replacePlaceHolders(maxOligoReachedHint, { [I18n.PlaceHolderKeys.MAX_OLIGO_COUNT] : `${maxNoOfOligos}` });
    return {
      reachedMaxNoOfOligos,
      ADD_OLIGO_LABEL: vocabulary[I18n.DictionaryKeys.PROJECT_FOOTER_ADD_OLIGO_BUTTON] || this.ADD_OLIGO_LABEL,
      MAX_OLIGO_REACHED_HINT: maxOligoReachedHint,
      NO_OF_OLIGO_LABEL: vocabulary[I18n.DictionaryKeys.PROJECT_FOOTER_No_OF_OLIGO_LABEL] || this.NO_OF_OLIGO_LABEL,
    };
  }

}

export const ProjectFooter: ng.IDirectiveFactory = () => {
  return {
    template: require('./project-footer.tpl.html'),
    controller: ProjectFooterController,
    controllerAs: 'projectFooter',
    bindToController: true,
    restrict: 'E',
    scope: {
      project: '<',
    },
    link: ($scope: ng.IScope, $elem: JQLite, attrs: ng.IAttributes, ctrl?: ng.IController) => {
      const controller: ProjectFooterController = ctrl as ProjectFooterController;
      let addOligoButtonHintJQ: Model.BootstrapJQ | null;
      const unwatchOligoThreshold = $scope.$watch(() => controller.reachedMaxNoOfOligos, (reachedThreshold: boolean) => {
        if (!addOligoButtonHintJQ) {
          const addOligoHintPopover: Model.BootstrapJQ = $elem.find('.btn.btn-outline-primary') as Model.BootstrapJQ;
          if (typeof addOligoHintPopover.popover === 'function') {
            addOligoButtonHintJQ = addOligoHintPopover;
          }
        }
        if (addOligoButtonHintJQ) {
          if (reachedThreshold) {
            const { MAX_OLIGO_REACHED_HINT: content } = controller;
            const popover: Model.BootstrapPopoverOptions = {
              content,
              html: true,
              placement: 'top',
              trigger: 'hover',
            };
            addOligoButtonHintJQ.popover(popover);
          } else {
            addOligoButtonHintJQ.popover('destroy');
          }
        }
      });
      $scope.$on('$destroy', () => {
        unwatchOligoThreshold();
        if (addOligoButtonHintJQ) {
          addOligoButtonHintJQ.popover('destroy');
          addOligoButtonHintJQ = null;
        }
      });
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/components/project-detail/project-footer/project-footer.ts